import React, { useState, useEffect, useMemo } from 'react';
import OrderNotes from "assets/images/brands/OrderNotes.png"
import { showError } from "toast/toast";
import ProjectFilePreviewModal, { type ProjectFilePreviewModalData } from "components/ProjectFilePreviewModal";
import { ViewIconButton, DownloadIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../utils/authCookies";

type InstructionsMode = "instructions" | "instructions_files";

type InstructionAttachment = {
  id?: string;
  name: string;
  sizeLabel?: string;
  previewUrl?: string;
  href?: string;
  dateLabel?: string;
};

type LoginType = "signage" | "brokerage" | "agent";

type LocalInstructionAttachment = InstructionAttachment & {
  localId: string;
  file: File;
  previewUrl: string;
};

interface InstructionsProps {
  title?: string;
  instructions?: string;
  placeholder?: string;
  rows?: number;
  onSave?: (
    instructions: string,
    context?: { hasPendingFiles: boolean; pendingFiles?: File[] }
  ) => void | Promise<void>;
  /** When true, parent uploads via `onSave` context `pendingFiles` instead of internal upload. */
  uploadFilesViaOnSave?: boolean;
  onAfterFilesUploaded?: () => void | Promise<void>;
  className?: string;
  iconColor?: string;
  iconBackground?: string;
  headerBgColor?: string;
  mode?: InstructionsMode;
  attachments?: InstructionAttachment[];
  onFilesSelected?: (files: FileList) => void;
  onAttachmentPreview?: (attachment: InstructionAttachment) => void;
  onAttachmentDownload?: (attachment: InstructionAttachment) => void;
  onAttachmentRemove?: (attachment: InstructionAttachment) => void;
  loginType?: LoginType;
  orderDocumentId?: string;
  collectionName?: string;
}

const Instructions: React.FC<InstructionsProps> = ({
  title = "Instructions",
  instructions = "",
  placeholder = "Add any notes related to this order...",
  rows = 6,
  onSave,
  onAfterFilesUploaded,
  uploadFilesViaOnSave = false,
  className = "",
  iconColor = "#10B981",
  iconBackground = "#EAF7F1",
  headerBgColor,
  mode = "instructions",
  attachments = [],
  onFilesSelected,
  onAttachmentPreview,
  onAttachmentDownload,
  onAttachmentRemove,
  loginType,
  orderDocumentId,
  collectionName,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(instructions);
  const [originalValue, setOriginalValue] = useState(instructions);
  const [localAttachments, setLocalAttachments] = useState<LocalInstructionAttachment[]>([]);
  const [previewData, setPreviewData] = useState<ProjectFilePreviewModalData | null>(null);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>([]);
  const uploadInputId = `instructions-upload-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const MAX_FILES = 3;
  const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 9 * 1024 * 1024;
  const hasPendingUploadedFiles = localAttachments.length > 0;

  // Update local state when props change
  useEffect(() => {
    setEditValue(instructions);
    setOriginalValue(instructions);
  }, [instructions]);

  const serverAttachmentIdsKey = useMemo(
    () =>
      (attachments ?? [])
        .map((a) => String(a.id ?? "").trim())
        .filter(Boolean)
        .sort()
        .join(","),
    [attachments]
  );

  useEffect(() => {
    setDeletedAttachmentIds((prev) => {
      if (!prev.length) return prev;
      const allowed = new Set(
        (attachments ?? []).map((a) => String(a.id || "").trim()).filter(Boolean)
      );
      const next = prev.filter((id) => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [serverAttachmentIdsKey, attachments]);

  useEffect(() => {
    return () => {
      localAttachments.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [localAttachments]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(instructions);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(originalValue);
    clearPendingUploadedFiles();
  };

  const handleSave = async () => {
    try {
      const hasPendingFiles = localAttachments.length > 0;
      const pendingFiles = hasPendingFiles
        ? localAttachments.map((item) => item.file)
        : undefined;
      if (onSave) {
        await onSave(editValue, { hasPendingFiles, pendingFiles });
      }
      const uploaded = uploadFilesViaOnSave
        ? hasPendingFiles
        : await handleSaveUploadedFiles();
      if (uploaded) {
        clearPendingUploadedFiles();
        await onAfterFilesUploaded?.();
      }
      setIsEditing(false);
      setOriginalValue(editValue);
    } catch (e: any) {
      showError(e?.message || "Failed to save instructions");
    }
  };

  const clearPendingUploadedFiles = () => {
    localAttachments.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setLocalAttachments([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const isFilesMode = mode === "instructions_files";

  const allAttachments = useMemo(
    () => [...attachments, ...localAttachments.map(({ localId, file, previewUrl, ...rest }) => rest)],
    [attachments, localAttachments]
  );
  const visibleAttachments = useMemo(
    () => allAttachments.filter((item) => !deletedAttachmentIds.includes(String(item.id || ""))),
    [allAttachments, deletedAttachmentIds]
  );

  const formatSizeLabel = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  const resolvedLoginType = useMemo<LoginType>(() => {
    if (loginType) return loginType;
    return "agent";
  }, [loginType]);

  const canShowDelete = resolvedLoginType === "signage";

  const getAttachmentIconClass = (fileName: string) => {
    const ext = String(fileName || "").split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return "ri-file-text-line text-danger fs-20";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext)) return "ri-file-image-line text-primary fs-20";
    return "ri-file-line text-secondary fs-20";
  };

  const handleFilesSelected = (files: FileList) => {
    const fileArray = Array.from(files);
    const existingCount = visibleAttachments.length;

    if (existingCount + fileArray.length > MAX_FILES) {
      showError("You can upload up to 3 files only.");
      return;
    }

    const oversize = fileArray.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversize) {
      showError(`Each file must be 3MB or less. "${oversize.name}" is too large.`);
      return;
    }

    const selectedTotal = fileArray.reduce((sum, f) => sum + f.size, 0);
    if (selectedTotal > MAX_TOTAL_BYTES) {
      showError("Total upload size cannot exceed 9MB.");
      return;
    }

    const newLocal = fileArray.map((file) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      localId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      sizeLabel: formatSizeLabel(file.size),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setLocalAttachments((prev) => [...prev, ...newLocal]);
  };

  const handleSaveUploadedFiles = async (): Promise<boolean> => {
    if (localAttachments.length === 0) return false;

    const token = getAuthCookie("sessionToken") || "";
    const id = String(orderDocumentId || "").trim();
    const resolvedCollectionName = String(collectionName || "").trim();

    // Existing order flows: upload directly from Instructions to avoid redundant parent calls.
    if (token && id && resolvedCollectionName) {
      const fd = new FormData();
      fd.append("id", id);
      fd.append("collection_name", resolvedCollectionName);
      localAttachments.forEach((item) => fd.append("files[]", item.file));

      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const raw = await res.json().catch(() => ({}));
        const msg =
          (raw as any)?.message ||
          (raw as any)?.error ||
          `Upload failed (${res.status})`;
        throw new Error(typeof msg === "string" ? msg : "Upload failed");
      }
      return true;
    }

    // Create-order flows (no id yet): keep existing parent callback behavior.
    if (onFilesSelected) {
      const dt = new DataTransfer();
      localAttachments.forEach((item) => dt.items.add(item.file));
      onFilesSelected(dt.files);
      return true;
    }

    return false;
  };

  const removeAttachment = async (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    if (local) {
      URL.revokeObjectURL(local.previewUrl);
      setLocalAttachments((prev) => prev.filter((item) => item.id !== attachment.id));
      onAttachmentRemove?.(attachment);
      return;
    }

    if (resolvedLoginType !== "signage") return;

    const token = getAuthCookie("sessionToken") || "";
    const id = String(orderDocumentId || "").trim();
    const fileId = String(attachment.id || "").trim();
    const resolvedCollectionName = String(collectionName || "").trim();
    if (!token || !id || !fileId || !resolvedCollectionName) {
      showError("Missing details to remove attachment.");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,
          collection_name: resolvedCollectionName,
          file_id: fileId,
        }),
      });
      if (!res.ok) {
        const raw = await res.json().catch(() => ({}));
        const msg = (raw as any)?.message || (raw as any)?.error || "Failed to remove attachment";
        throw new Error(msg);
      }
      setDeletedAttachmentIds((prev) => [...prev, fileId]);
      onAttachmentRemove?.(attachment);
    } catch (e: any) {
      showError(e?.message || "Failed to remove attachment");
    }
  };

  const previewAttachment = (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    const modalData: ProjectFilePreviewModalData = {
      name: attachment.name,
      previewUrl: local?.previewUrl || attachment.previewUrl || "",
      href: local?.previewUrl || attachment.href || attachment.previewUrl || "",
      sizeLabel: attachment.sizeLabel,
      dateLabel: attachment.dateLabel,
    };
    setPreviewData(modalData);
    onAttachmentPreview?.(attachment);
  };

  const downloadAttachment = (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    if (local) {
      const a = document.createElement("a");
      a.href = local.previewUrl;
      a.download = local.name;
      a.click();
    } else if (attachment.href || attachment.previewUrl) {
      const a = document.createElement("a");
      a.href = attachment.href || attachment.previewUrl || "";
      a.download = attachment.name;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
    }
    onAttachmentDownload?.(attachment);
  };

  return (
    <div className={`card shadow-lg shadow-border-1 ${className}`}>
      {
        <style>{`
            .cancel-btn {
                background: #f1f3f5;
                font-size: 13px;
            }
            .cancel-btn:hover {
                background: #e9ebef;
                font-weight: 600;
            }
        `}</style>
      }
      <div className="py-2 px-3 rounded-top" style={{ height: "54px", ...(headerBgColor ? { backgroundColor: headerBgColor } : {}) }}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 d-flex align-items-center fw-semibold">
            <span
              aria-hidden="true"
              className="image-icons"
              style={{
                backgroundColor: "#009966",
                WebkitMaskImage: `url(${OrderNotes})`,
                maskImage: `url(${OrderNotes})`,
              }}
            />

            <span className='ms-2'>{title}</span>
          </h6>

          <div className="d-flex align-items-center gap-2">
            {!(isEditing || hasPendingUploadedFiles) ? (
              <button
                type="button"
                className="bg-white border border-2 btn btn-link p-0 px-1 text-muted"
                onClick={handleEdit}
                title="Edit Instructions"
              >
                <i className="fs-20 ri-edit-line text-black" title="Edit"></i>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-light border px-3 py-2 br-8 cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary table-addbutton px-3 py-2 br-8"
                  onClick={handleSave}
                  disabled={!editValue.trim() && !hasPendingUploadedFiles}
                >
                  Save
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card-body">
        {(isEditing || hasPendingUploadedFiles) ? (
          <div>
            <textarea
              className="form-control bg-light rounded-3 p-3 fs-6 lh-base"
              style={{ resize: 'none' }}
              rows={rows}
              placeholder={placeholder}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
        ) : (
          <div
            className={`bg-light border rounded-3 p-3 ${!instructions ? "fst-italic text-muted" : "text-dark"}`}
            style={{
              minHeight: `${rows * 1.5}rem`,
              lineHeight: "1.5",
              backgroundColor: "#f8f9fa",
              borderColor: "#dee2e6",
              color: instructions ? "#212529" : undefined,
            }}
          >
            {instructions ? (
              <div className="text-break">{instructions}</div>
            ) : (
              <span className="text-muted">{placeholder}</span>
            )}
          </div>
        )}

        {isFilesMode && (
          <div className="mt-3">
            <hr className="my-3" />

            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center gap-2 fw-medium">
                <i className="ri-attachment-2" />
                <span>Attachments</span>
                <span className="badge rounded-pill text-bg-secondary">{visibleAttachments.length}</span>
              </div>

              {canShowDelete ? (
                <label htmlFor={uploadInputId} className="btn btn-link p-0 text-decoration-none fw-medium">
                  <i className="ri-upload-cloud-2-line me-1" />
                  Upload File
                </label>
              ) : null}
              {canShowDelete ? (
                <input
                  id={uploadInputId}
                  type="file"
                  className="d-none"
                  multiple
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleFilesSelected(e.target.files);
                    }
                    e.currentTarget.value = "";
                  }}
                />
              ) : null}
            </div>

            <div className="d-flex flex-column gap-2">
              {visibleAttachments.map((file: InstructionAttachment, index: number) => (
                <div
                  key={file.id || `${file.name}-${index}`}
                  className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 instructions-attachment-row"
                >
                  <div className="d-flex align-items-center gap-2 overflow-hidden">
                    <i className={getAttachmentIconClass(file.name)} />
                    <div className="text-truncate">
                      <div className="text-truncate fw-medium">{file.name}</div>
                      {file.sizeLabel ? <div className="small text-muted">{file.sizeLabel}</div> : null}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 ms-2 instructions-attachment-actions">
                    <ViewIconButton
                      size={30}
                      title="View"
                      ariaLabel="View file"
                      onClick={() => previewAttachment(file)}
                    />
                    <DownloadIconButton
                      size={30}
                      title="Download"
                      ariaLabel="Download file"
                      onClick={() => downloadAttachment(file)}
                    />
                    {canShowDelete ? (
                      <DeleteIconButton
                        size={30}
                        title="Remove"
                        ariaLabel="Remove file"
                        onClick={() => removeAttachment(file)}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {canShowDelete ? (
              <label
                htmlFor={uploadInputId}
                className="mt-3 rounded-3 text-center py-4 w-100 cursor-pointer instructions-upload-dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    handleFilesSelected(e.dataTransfer.files);
                  }
                }}
              >
                <i className="ri-upload-cloud-2-line fs-4 text-muted" />
                <div className="fw-medium" style={{ color: "#2563EB" }}>Click to upload <span className="text-muted">or drag & drop</span></div>
                <div className="small text-muted">PDF, PNG, JPG, XLSX up to 3MB each (max 3 files / 9MB total)</div>
              </label>
            ) : null}

          </div>
        )}
      </div>

      {/* Minimal custom CSS - only for what Bootstrap can't handle */}
      <style>{`
        /* Custom border radius that Bootstrap doesn't have */
        .rounded-4 { border-radius: 16px; }
        
        /* Smooth transitions for better UX */
        .card { transition: all 0.2s ease-in-out; }
        
        /* Custom icon chip dimensions */
        .icon-chip-custom { 
          width: 28px; 
          height: 28px; 
        }
        .instructions-upload-dropzone {
          border: 2px dashed #d1d5db;
          background-color: #f3f4f6;
          transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
        }
        .instructions-upload-dropzone:hover {
          background-color: #e5e7eb;
          border-color: #9ca3af;
        }
        .instructions-attachment-row {
          border: 1px solid transparent;
          background-color: #f3f4f6;
          transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
        }
        .instructions-attachment-row:hover {
          border-color: #d1d5db;
          background-color: #eceff3;
        }
        .instructions-attachment-actions {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease-in-out;
        }
        .instructions-attachment-row:hover .instructions-attachment-actions {
          opacity: 1;
          pointer-events: auto;
        }
      `}</style>

      <ProjectFilePreviewModal
        data={previewData}
        onClose={() => setPreviewData(null)}
        onDownload={() => {
          if (!previewData) return;
          downloadAttachment({
            name: previewData.name,
            previewUrl: previewData.previewUrl,
            href: previewData.href,
            sizeLabel: previewData.sizeLabel,
            dateLabel: previewData.dateLabel,
          });
        }}
      />
    </div>
  );
};

export default Instructions;
